import type { CreateMissionTO, MissionTO } from '@control-center/control-center-client-public-missions-ts';
import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { missionFromRestV2 } from 'domain/mappers/missionFromRestV2';
import type { MissionFE } from 'domain/mission';

import { normaliseCommandError, type ServiceCommandError } from './commandFeedback';
import { errorHandler, formatAxiosError, isAxiosError } from './errors';
import { correlationHeaders, missionsApi } from './restClient';

/**
 * The five mission commands run against the Mission and Hub Service over REST
 * (`VITE_MISSION_HUB_SERVICE_ENDPOINT`), not AppSync. Each endpoint answers with a full
 * `MissionTO`, which goes through the shared `missionFromRestV2` mapper so callers receive the
 * domain `MissionFE`.
 *
 * This slice holds nothing but these five mutations. The GraphQL `onMissionV2Subscription` entry
 * it used to carry had no consumer at all and was removed with the mission query migration; the
 * one live mission subscription is `onMissionV3Subscription` in `./missionV3`.
 *
 * Error convention, copied from `createVehicle`/`updateVehicle` in `vehiclesV2.ts`: a mutation
 * RETURNS an RTK Query error object when the service answered with an HTTP status, so the
 * component can read that status, and only throws when there is no response to report.
 */
type MissionMutationResult = { data: MissionFE } | { error: ServiceCommandError };

/** `{ missionId }`, so the four action components keep passing the argument they always have. */
type MissionIdArg = { missionId: string };

/**
 * Shared body for all five commands. `command` is typed against `MissionTO` rather than `unknown`
 * so a spec change to the client's return type breaks the build here, even though
 * `missionFromRestV2` accepts `unknown` and validates at runtime.
 */
const missionCommandQueryFn = async (
    command: () => Promise<{ data: MissionTO }>,
    errorMessage: string
): Promise<MissionMutationResult> => {
    try {
        const { data } = await command();
        return { data: missionFromRestV2(data) };
    } catch (error) {
        const loggableError = isAxiosError(error) ? formatAxiosError(error) : error;
        const handledError = errorHandler(errorMessage, loggableError);

        const serviceError = normaliseCommandError(error, handledError.message);
        if (serviceError.status !== undefined) {
            return { error: serviceError };
        }

        throw handledError;
    }
};

/** Creates (assigns) a mission for a vehicle and returns the created mission. */
const createMissionMutationFn = (variables: CreateMissionTO) =>
    missionCommandQueryFn(
        () => missionsApi.createMission(variables, correlationHeaders()),
        `Unexpected error occurred while creating the mission with vin: ${variables.vin} and predefinedRouteId ${variables.predefinedRouteId}`
    );

/** Starts a mission by ID and returns the updated mission. */
const startMissionMutationFn = ({ missionId }: MissionIdArg) =>
    missionCommandQueryFn(
        () => missionsApi.startMission(missionId, correlationHeaders()),
        `Unexpected error occurred while starting the mission with id: ${missionId}`
    );

/** Cancels a mission by ID and returns the canceled mission. */
const cancelMissionMutationFn = ({ missionId }: MissionIdArg) =>
    missionCommandQueryFn(
        () => missionsApi.cancelMission(missionId, correlationHeaders()),
        `Unexpected error occurred while canceling the mission with id: ${missionId}`
    );

/** Archives a mission by ID and returns the archived mission. */
const archiveMissionMutationFn = ({ missionId }: MissionIdArg) =>
    missionCommandQueryFn(
        () => missionsApi.archiveMission(missionId, correlationHeaders()),
        `Unexpected error occurred while archiving the mission with id: ${missionId}`
    );

/** Resets a mission by ID and returns the reset mission. */
const resetMissionMutationFn = ({ missionId }: MissionIdArg) =>
    missionCommandQueryFn(
        () => missionsApi.resetMission(missionId, correlationHeaders()),
        `Unexpected error occurred while resetting the mission with id: ${missionId}`
    );

export const missionV2Api = createApi({
    reducerPath: 'missionV2',
    // All endpoints use `queryFn`, so the base query is never called. `fakeBaseQuery` ensures a
    // future endpoint that omits `queryFn` fails loudly instead of silently POSTing to `/graphql`.
    baseQuery: fakeBaseQuery<unknown>(),
    refetchOnMountOrArgChange: true,
    endpoints: builder => ({
        createMission: builder.mutation<MissionFE, CreateMissionTO>({
            queryFn: createMissionMutationFn,
        }),
        startMission: builder.mutation<MissionFE, MissionIdArg>({
            queryFn: startMissionMutationFn,
        }),
        cancelMission: builder.mutation<MissionFE, MissionIdArg>({
            queryFn: cancelMissionMutationFn,
        }),
        archiveMission: builder.mutation<MissionFE, MissionIdArg>({
            queryFn: archiveMissionMutationFn,
        }),
        resetMission: builder.mutation<MissionFE, MissionIdArg>({
            queryFn: resetMissionMutationFn,
        }),
    }),
});

export const {
    useCreateMissionMutation,
    useStartMissionMutation,
    useCancelMissionMutation,
    useArchiveMissionMutation,
    useResetMissionMutation,
} = missionV2Api;
